import { __rest, __assign } from 'tslib';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { faThumbtack, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTransition, config, animated } from '@react-spring/web';
import { classNames } from '../../utils/classNames/classNames.esm.js';
import { noop } from '../../utils/noop.esm.js';
import '@popperjs/core';
import { testIdGen } from '../../utils/testIdGen.esm.js';
import { Button } from '../Button/Button.esm.js';
import '../Icon/ImgIcon.esm.js';
import { FaIcon } from '../Icon/FaIcon.esm.js';
import '../Icon/SemanticIcons.esm.js';
import '../Button/ProgressButton.esm.js';
import '../Button/CopyButton.esm.js';
import { Body } from '../Typography/Body.esm.js';
import '../Tooltip/Tooltip.esm.js';
import { Title } from '../Typography/Title.esm.js';

var sidecarSizes = {
    sm: 400,
    md: 600,
    lg: 800
};
/** This component renders a sidecar that can be pinned to its container or overlay the whole page.
 *
 * Make sure to place it in a * flex container next to your content.
 * The parent element will need to have a `relative` position.
 */
function Sidecar(_a) {
    var children = _a.children, dataMetrics = _a.dataMetrics, dataTestId = _a.dataTestId, _b = _a.showMask, showMask = _b === void 0 ? false : _b, title = _a.title, subtitle = _a.subtitle, icon = _a.icon, _c = _a.disablePinnedChange, disablePinnedChange = _c === void 0 ? false : _c, _d = _a.initialPinned, initialPinned = _d === void 0 ? true : _d, _e = _a.onPinnedChange, onPinnedChange = _e === void 0 ? noop : _e, actions = _a.actions, _f = _a.isOpen, isOpen = _f === void 0 ? false : _f, onClose = _a.onClose, _g = _a.size, size = _g === void 0 ? "sm" : _g, passThrough = __rest(_a, ["children", "dataMetrics", "dataTestId", "showMask", "title", "subtitle", "icon", "disablePinnedChange", "initialPinned", "onPinnedChange", "actions", "isOpen", "onClose", "size"]);
    var intl = useIntl();
    var width = sidecarSizes[size];
    var _h = useState(initialPinned), pinned = _h[0], setPinned = _h[1];
    var sidecarClasses = classNames("tw-box-border tw-font-lato tw-bg-white tw-shadow tw-border-l tw-z-10 dark:tw-bg-gray-1000 dark:tw-border-gray-700", "tw-flex tw-flex-col tw-overflow-hidden", pinned && "tw-relative tw-flex-shrink-0", !pinned && "tw-h-full tw-fixed tw-top-0 tw-right-0");
    var transitions = useTransition(isOpen, {
        from: { width: 0 },
        enter: { width: width },
        update: { width: width },
        leave: { width: 0 },
        config: __assign(__assign({}, config.stiff), { clamp: true })
    });
    var datatestid = dataTestId || (title && "sidecar-".concat(testIdGen(title)));
    return (React.createElement(React.Fragment, null,
        showMask && (React.createElement(OverlayMask, { isOpen: isOpen, onClick: onClose, label: intl.formatMessage({ id: "Wg61eK", defaultMessage: [{ type: 0, value: "Close" }] }), pinned: pinned })),
        transitions(function (styles, item) {
            return item && (React.createElement(animated.div, __assign({ "data-metrics": dataMetrics, "data-testid": datatestid, style: styles, className: sidecarClasses }, passThrough),
                React.createElement("div", { className: "tw-flex tw-flex-col tw-h-full" },
                    React.createElement("div", { className: "tw-border-solid tw-border-gray-200 tw-flex tw-justify-between tw-px-4 tw-py-2 tw-space-x-2 tw-border-b dark:tw-border-gray-700" },
                        React.createElement("div", { 
                            // Both tw-min-w-0 here and below are required for text truncating to work
                            className: "tw-flex tw-space-x-1 tw-items-center tw-min-w-0" },
                            icon && (React.createElement("div", { 
                                // Avoid the icon being squeezed when the title is long
                                className: "tw-flex-shrink-0" }, icon)),
                            React.createElement("div", { className: "tw-min-w-0 tw-flex tw-flex-col" },
                                React.createElement(Title, { level: 2, size: "xxs", addClassName: "tw-flex", truncate: true }, title),
                                subtitle && (React.createElement(Body, { size: "sm", addClassName: "tw-flex tw-text-secondary", truncate: true }, subtitle)))),
                        React.createElement("div", { className: "tw-flex tw-space-x-2" },
                            actions,
                            !disablePinnedChange && (React.createElement(Button, { appearance: "tertiary-clear", size: "sm", onClick: function () {
                                    onPinnedChange === null || onPinnedChange === void 0 ? void 0 : onPinnedChange(!pinned);
                                    setPinned(function (pinned) { return !pinned; });
                                }, "aria-label": intl.formatMessage({ id: "6yHH5K", defaultMessage: [{ type: 0, value: "Pin" }] }), isSelected: pinned, icon: React.createElement(FaIcon, { faIconDef: faThumbtack }) })),
                            React.createElement(Button, { onClick: onClose, appearance: "tertiary-clear", "aria-label": intl.formatMessage({ id: "Wg61eK", defaultMessage: [{ type: 0, value: "Close" }] }), size: "sm", icon: React.createElement(FaIcon, { faIconDef: faTimes }) }))),
                    React.createElement("div", { className: "tw-flex tw-flex-col tw-overflow-auto tw-flex-grow" }, children))));
        })));
}
/**
 * The OverlayMask component is used to create a mask, often behind a Modal or some other popup.
 */
function OverlayMask(_a) {
    var _b = _a.pinned, pinned = _b === void 0 ? false : _b, isOpen = _a.isOpen, label = _a.label, onClick = _a.onClick;
    var transitions = useTransition(isOpen, {
        from: { opacity: 0 },
        enter: { opacity: 0.5 },
        leave: { opacity: 0 },
        config: { clamp: true }
    });
    var classes = classNames("tw-inset-0 tw-bg-black", pinned ? "tw-absolute" : "tw-fixed", onClick && "tw-cursor-default");
    return transitions(function (styles, item) {
        return item && (React.createElement(animated.div, { style: styles, className: classes, "aria-label": label, onClick: onClick }));
    });
}

export { OverlayMask, Sidecar, sidecarSizes };
//# sourceMappingURL=Sidecar.esm.js.map
